// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
//
// SVG halos renderer — highlight and selection rings.
//
// This module intentionally merges the highlight and selection renderers: both
// draw the same counter-scaled ring and differ only in which ids they
// cover, so one renderHalos() over the union of selectedIds + highlightedId
// is simpler.

import { el, rgba } from "./dom.js";

/**
 * Compute the halo ring radius in tree-coordinate units for a given node.
 *
 * Collapsed nodes reuse the leaf ring formula: there is no dedicated
 * collapsed-node marker (collapsed subtrees appear only via piecharts),
 * so they fall back to the leaf halo size.
 *
 * @param {object} node - Graph node ({ isLeaf, isCollapsed }).
 * @param {object} props - Props with haloWidth, nodeSize, haloRadius.
 * @returns {number} Radius in tree-coordinate (logical) units.
 */
export function haloRadius(node, props) {
  const width = props.haloWidth ?? 4;
  const nodeSize = props.nodeSize ?? 14;
  const radius = props.haloRadius ?? 12;

  if (node.isLeaf || node.isCollapsed) {
    return Math.max(nodeSize * 0.75, radius) + width / 2;
  }
  // Expanded internal node
  return width + width / 2;
}

/**
 * Render halo rings for highlighted and selected nodes.
 *
 * Each ring is an unfilled <circle> counter-scaled by 1/scale so it appears
 * at a constant screen-pixel size regardless of zoom level.
 *
 * Halos live INSIDE the zoomed contentGroup (laid out in tree coordinates),
 * so they track nodes during pan for free. Only when scale changes do the
 * r and stroke-width need to be recomputed.
 *
 * Z-position in the layer stack: between piecharts and branch labels
 * (see spec §6 for full z-order).
 *
 * Always returns a <g class="halos"> element (never null) so it is a
 * stable z-anchor that can be replaced in-place via view.replaceHalos().
 *
 * @param {object} graph - Built graph (graph.nodeById).
 * @param {object} props - Props (selectedIds, highlightedId, highlightColour, haloWidth).
 * @param {number} scale - Current view scale (svg.clientWidth / viewBox.w).
 * @returns {SVGGElement} A <g class="halos"> element.
 */
export function renderHalos(graph, props, scale) {
  if (!graph || !graph.nodeById) {
    return el("g", { class: "halos" });
  }

  // Let rgba() bake alpha into the stroke colour (matches edges/shapes/
  // piecharts) instead of splitting it into a separate stroke-opacity.
  const strokeColour = rgba(props.highlightColour ?? [60, 115, 131, 255], false);
  const strokeWidth = (props.haloWidth ?? 4) / scale;

  // Collect node ids to draw — deduplicate if highlightedId is also selected.
  const ids = new Set(props.selectedIds ?? []);
  if (props.highlightedId != null) {
    ids.add(props.highlightedId);
  }

  const circles = [];
  for (const id of ids) {
    const node = graph.nodeById[id];
    if (!node || node.isHidden) continue;

    const r = haloRadius(node, props) / scale;

    circles.push(
      el("circle", {
        cx: node.x,
        cy: node.y,
        r,
        fill: "none",
        stroke: strokeColour,
        "stroke-width": strokeWidth,
        "data-node-id": id,
      }),
    );
  }

  return el("g", { class: "halos" }, circles);
}
